import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useJobListings, type JobListing } from '@/hooks/useJobListings';
import { supabase } from '@/lib/supabase';

const DEFAULT_PASSWORD = 'tmt2024';
const PASSWORD_STORAGE_KEY = 'tmt_admin_password';

function getStoredPassword(): string {
  return localStorage.getItem(PASSWORD_STORAGE_KEY) || DEFAULT_PASSWORD;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type CareerApplication = {
  id: number;
  position: string;
  name: string;
  email: string;
  phone: string | null;
  linkedin: string | null;
  cover_letter: string | null;
  message: string | null;
  cv_filename: string | null;
  created_at: string;
};

function downloadCSV(headers: string[], rows: string[][], filename: string) {
  const escapeField = (field: string) => {
    if (field.includes(',') || field.includes('"') || field.includes('\n') || field.includes('\r')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  };

  const bom = '\uFEFF';
  const csvContent = [
    headers.map(escapeField).join(','),
    ...rows.map((row) => row.map(escapeField).join(',')),
  ].join('\n');

  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const iconOptions = [
  'ri-code-s-slash-line',
  'ri-scales-3-line',
  'ri-bank-card-line',
  'ri-palette-line',
  'ri-brain-line',
  'ri-customer-service-2-line',
  'ri-global-line',
  'ri-shield-check-line',
  'ri-bar-chart-box-line',
  'ri-team-line',
  'ri-bar-chart-box-line',
  'ri-team-line',
  'ri-megaphone-line',
  'ri-megaphone-line',
];

const emptyForm = {
  title: '',
  department: '',
  location: '',
  type: 'Full-time',
  description: '',
  requirements: [''],
  icon: 'ri-team-line',
};

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === getStoredPassword()) {
      onUnlock();
    } else {
      setError(true);
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border-2 border-primary-600/40 rounded-full">
            <i className="ri-lock-line text-primary-400 text-xl" />
          </div>
          <h1 className="font-heading text-2xl text-foreground-50 mb-2">Admin Access</h1>
          <p className="text-foreground-500 text-sm font-body">Enter the password to manage career positions.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(false); }}
              placeholder="Enter password"
              autoFocus
              className={`w-full px-4 py-3.5 bg-background-50 border rounded-xl text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none transition-colors pr-12 ${
                error ? 'border-red-400/50' : 'border-primary-700/30 focus:border-primary-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 hover:text-foreground-300 transition-colors"
            >
              <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
            </button>
          </div>

          {error && (
            <p className="text-red-400 text-xs font-body text-center">Incorrect password. Please try again.</p>
          )}

          <button
            type="submit"
            disabled={!password}
            className="w-full py-3.5 bg-primary-500 text-background-50 rounded-xl font-label text-sm font-semibold tracking-wider hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            Unlock Panel
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/careers"
            className="text-foreground-500 text-xs font-body hover:text-foreground-300 transition-colors"
          >
            <i className="ri-arrow-left-line mr-1" />
            Back to Careers
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AdminCareersPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState<'positions' | 'applications'>('positions');
  const { jobs, departments, addJob, updateJob, deleteJob, resetToDefault } = useJobListings();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  // Career applications state
  const [applications, setApplications] = useState<CareerApplication[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsSearch, setAppsSearch] = useState('');
  const [confirmDeleteApp, setConfirmDeleteApp] = useState<number | null>(null);

  useEffect(() => {
    // No sessionStorage check — auth state resets every time the page loads
  }, []);

  const loadApplications = async () => {
    setAppsLoading(true);
    try {
      const { data, error } = await supabase
        .from('career_applications')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setApplications(data || []);
    } catch (err) {
      console.error('Failed to load career applications:', err);
    } finally {
      setAppsLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated && activeTab === 'applications') {
      loadApplications();
    }
  }, [authenticated, activeTab]);

  const handleDeleteApp = async (id: number) => {
    try {
      const { error } = await supabase.from('career_applications').delete().eq('id', id);
      if (error) throw error;
      setApplications((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Failed to delete application:', err);
    } finally {
      setConfirmDeleteApp(null);
    }
  };

  const filteredApps = applications.filter(
    (a) =>
      a.name.toLowerCase().includes(appsSearch.toLowerCase()) ||
      a.email.toLowerCase().includes(appsSearch.toLowerCase()) ||
      a.position.toLowerCase().includes(appsSearch.toLowerCase())
  );

  const handleLogout = () => {
    setAuthenticated(false);
  };

  const openPasswordChange = () => {
    setPwCurrent('');
    setPwNew('');
    setPwConfirm('');
    setPwError('');
    setPwSuccess(false);
    setShowPasswordChange(true);
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');

    if (pwCurrent !== getStoredPassword()) {
      setPwError('Current password is incorrect.');
      return;
    }
    if (pwNew.length < 4) {
      setPwError('New password must be at least 4 characters.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('New passwords do not match.');
      return;
    }

    localStorage.setItem(PASSWORD_STORAGE_KEY, pwNew);
    setPwSuccess(true);
    setTimeout(() => {
      setShowPasswordChange(false);
      setPwSuccess(false);
    }, 1500);
  };

  const openAdd = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setShowForm(true);
    setConfirmDelete(null);
  };

  const openEdit = (job: JobListing) => {
    setForm({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      description: job.description,
      requirements: [...job.requirements],
      icon: job.icon,
    });
    setEditingId(job.id);
    setShowForm(true);
    setConfirmDelete(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
    setConfirmDelete(null);
  };

  const handleSubmit = useCallback(() => {
    const trimmedReqs = form.requirements.map((r) => r.trim()).filter((r) => r.length > 0);
    if (!form.title.trim() || !form.department.trim() || !form.location.trim() || !form.description.trim()) return;

    const payload = {
      title: form.title.trim(),
      department: form.department.trim(),
      location: form.location.trim(),
      type: form.type,
      description: form.description.trim(),
      requirements: trimmedReqs.length > 0 ? trimmedReqs : ['No specific requirements listed.'],
      icon: form.icon,
    };

    if (editingId) {
      updateJob(editingId, payload);
    } else {
      addJob(payload);
    }
    closeForm();
  }, [form, editingId, addJob, updateJob]);

  const updateReq = (index: number, value: string) => {
    setForm((prev) => {
      const next = [...prev.requirements];
      next[index] = value;
      return { ...prev, requirements: next };
    });
  };

  const addReqField = () => {
    setForm((prev) => ({ ...prev, requirements: [...prev.requirements, ''] }));
  };

  const removeReqField = (index: number) => {
    setForm((prev) => {
      const next = prev.requirements.filter((_, i) => i !== index);
      return { ...prev, requirements: next.length > 0 ? next : [''] };
    });
  };

  if (!authenticated) {
    return <PasswordGate onUnlock={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-background-50 pt-24 md:pt-32 pb-12 px-4 md:px-8 lg:px-12">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-2">
              / Admin
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-50">Career Positions</h1>
            <p className="text-foreground-400 text-sm mt-1 font-body">Manage open positions. Changes save instantly to your browser.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleLogout}
              className="px-4 py-2 border border-primary-700/30 rounded-full font-label text-xs tracking-wider text-foreground-400 hover:text-red-400 hover:border-red-400/40 transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-lock-unlock-line" />
              Lock
            </button>
            <button
              onClick={openPasswordChange}
              className="px-4 py-2 border border-primary-700/30 rounded-full font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-key-line" />
              Password
            </button>
            <Link
              to="/careers"
              className="px-4 py-2 border border-primary-700/30 rounded-full font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 transition-colors whitespace-nowrap"
            >
              View Careers Page
            </Link>
            {activeTab === 'positions' && (
            <button
              onClick={openAdd}
              className="px-4 py-2 bg-primary-500 text-background-50 rounded-full font-label text-xs tracking-wider hover:bg-primary-400 transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-add-line" />
              Add Position
            </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8 border-b border-primary-800/20 pb-1">
          {[
            { id: 'positions' as const, label: 'Positions', icon: 'ri-briefcase-4-line', count: jobs.length },
            { id: 'applications' as const, label: 'Applications', icon: 'ri-file-user-line', count: applications.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 font-label text-xs tracking-wider rounded-t-lg transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? 'text-primary-400 border-b-2 border-primary-400'
                  : 'text-foreground-500 hover:text-foreground-300'
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                activeTab === tab.id ? 'bg-primary-500/15 text-primary-400' : 'bg-background-200 text-foreground-500'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Positions Tab */}
        {activeTab === 'positions' && (
          <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Positions', value: jobs.length },
            { label: 'Departments', value: departments.length - 1 },
            { label: 'Full-time', value: jobs.filter((j) => j.type === 'Full-time').length },
            { label: 'Remote', value: jobs.filter((j) => j.location.toLowerCase().includes('remote')).length },
          ].map((stat) => (
            <div key={stat.label} className="p-4 bg-background-100 border border-primary-800/20 rounded-xl text-center">
              <p className="font-heading text-2xl text-foreground-50">{stat.value}</p>
              <p className="text-foreground-500 text-xs font-label tracking-wider mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Job List */}
        <div className="space-y-3">
          {jobs.length === 0 && (
            <div className="text-center py-12 bg-background-100 border border-primary-800/20 rounded-xl">
              <p className="text-foreground-500 text-sm font-body mb-4">No positions yet. Add your first one.</p>
              <button
                onClick={openAdd}
                className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 transition-colors"
              >
                Add First Position
              </button>
            </div>
          )}

          {jobs.map((job, index) => (
            <div
              key={job.id}
              className="p-4 md:p-5 bg-background-100 border border-primary-800/20 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center border border-primary-600/30 rounded-lg bg-background-50">
                  <i className={`${job.icon} text-primary-400 text-lg`} />
                </div>
                <div>
                  <h3 className="font-heading text-base text-foreground-100">{job.title}</h3>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-foreground-500 text-xs font-label">
                    <span className="flex items-center gap-1"><i className="ri-building-line" /> {job.department}</span>
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line" /> {job.location}</span>
                    <span className="px-2 py-0.5 bg-primary-500/10 text-primary-400 rounded-full text-[0.6rem]">{job.type}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => openEdit(job)}
                  className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-lg text-foreground-400 hover:text-primary-400 hover:border-primary-500/40 transition-colors"
                  title="Edit"
                >
                  <i className="ri-edit-line" />
                </button>
                {confirmDelete === job.id ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { deleteJob(job.id); setConfirmDelete(null); }}
                      className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg font-label text-xs hover:bg-red-500/20 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="px-3 py-1.5 border border-primary-700/30 rounded-lg font-label text-xs text-foreground-400 hover:text-foreground-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(job.id)}
                    className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-lg text-foreground-400 hover:text-red-400 hover:border-red-400/40 transition-colors"
                    title="Delete"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

            {/* Reset button */}
            {jobs.length > 0 && (
              <div className="mt-8 text-center">
                <button
                  onClick={resetToDefault}
                  className="text-foreground-500 text-xs font-body hover:text-foreground-300 transition-colors underline underline-offset-2"
                >
                  Reset all positions to default
                </button>
              </div>
            )}
          </>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-lg text-foreground-100">CV Submissions</h2>
                <p className="text-foreground-500 text-xs font-body mt-1">
                  {applications.length} total application{applications.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
                  <input
                    type="text"
                    value={appsSearch}
                    onChange={(e) => setAppsSearch(e.target.value)}
                    placeholder="Search by name, email, position..."
                    className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <button
                  onClick={() => {
                    const data = appsSearch ? filteredApps : applications;
                    const headers = ['ID', 'Position', 'Name', 'Email', 'Phone', 'LinkedIn', 'Cover Letter', 'Message', 'CV Filename', 'Date'];
                    const rows = data.map((c) => [
                      String(c.id), c.position, c.name, c.email, c.phone || '', c.linkedin || '',
                      (c.cover_letter || '').replace(/\n/g, ' '), (c.message || '').replace(/\n/g, ' '),
                      c.cv_filename || '', formatDate(c.created_at),
                    ]);
                    downloadCSV(headers, rows, `career_applications_${new Date().toISOString().slice(0, 10)}.csv`);
                  }}
                  disabled={applications.length === 0}
                  className="px-4 py-2.5 border border-primary-700/30 rounded-lg font-label text-xs tracking-wider text-foreground-300 hover:text-primary-400 hover:border-primary-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-line" />
                  Export CSV
                </button>
              </div>
            </div>

            {appsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-400 rounded-full animate-spin" />
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="text-center py-16 bg-background-100 border border-primary-800/20 rounded-2xl">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-briefcase-line text-primary-400 text-xl" />
                </div>
                <p className="text-foreground-500 text-sm font-body">
                  {appsSearch ? 'No applications match your search.' : 'No applications yet. They\'ll appear here when someone applies via the Careers page.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredApps.map((app) => (
                  <div
                    key={app.id}
                    className="bg-background-100 border border-primary-800/20 rounded-xl p-5"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                          <i className="ri-user-line text-primary-400" />
                        </div>
                        <div>
                          <p className="font-heading text-sm text-foreground-100">
                            {app.name}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-foreground-500 text-xs font-body">
                            <span className="flex items-center gap-1">
                              <i className="ri-mail-line" /> {app.email}
                            </span>
                            {app.phone && (
                              <span className="flex items-center gap-1">
                                <i className="ri-phone-line" /> {app.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-foreground-600 text-xs font-body">
                          {formatDate(app.created_at)}
                        </span>
                        {confirmDeleteApp === app.id ? (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleDeleteApp(app.id)}
                              className="px-2.5 py-1 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md font-label text-[10px] hover:bg-red-500/20 transition-colors whitespace-nowrap"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmDeleteApp(null)}
                              className="px-2.5 py-1 border border-primary-700/30 rounded-md font-label text-[10px] text-foreground-400 hover:text-foreground-200 transition-colors whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteApp(app.id)}
                            className="w-7 h-7 flex items-center justify-center border border-primary-700/30 rounded-md text-foreground-500 hover:text-red-400 hover:border-red-400/40 transition-colors flex-shrink-0"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-900 rounded-md text-[11px] font-label tracking-wider">
                        {app.position}
                      </span>
                    </div>
                    {app.cover_letter && (
                      <p className="text-foreground-300 text-sm font-body leading-relaxed bg-background-50 rounded-lg p-3 mb-2">
                        {app.cover_letter}
                      </p>
                    )}
                    {app.message && (
                      <p className="text-foreground-300 text-sm font-body leading-relaxed bg-background-50 rounded-lg p-3 mb-2">
                        {app.message}
                      </p>
                    )}
                    {app.linkedin && (
                      <div className="flex items-center gap-1.5 text-xs font-body">
                        <i className="ri-linkedin-box-line text-foreground-500" />
                        <a
                          href={app.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-400 hover:text-primary-300 transition-colors"
                        >
                          View LinkedIn
                        </a>
                      </div>
                    )}
                    {app.cv_filename && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs font-body text-foreground-500">
                        <i className="ri-file-text-line" />
                        <span>CV: {app.cv_filename}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <div className="fixed inset-0 z-[70] bg-background-50/90 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-sm bg-background-50 border border-primary-800/20 rounded-2xl shadow-xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-foreground-50">Change Password</h2>
              <button
                onClick={() => setShowPasswordChange(false)}
                className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-full text-foreground-400 hover:text-foreground-100 hover:border-primary-500/40 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {pwSuccess ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/20">
                  <i className="ri-check-line text-primary-400 text-2xl" />
                </div>
                <p className="text-foreground-100 font-body text-sm">Password changed successfully!</p>
              </div>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Current Password</label>
                  <input
                    type="password"
                    value={pwCurrent}
                    onChange={(e) => { setPwCurrent(e.target.value); setPwError(''); }}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">New Password</label>
                  <input
                    type="password"
                    value={pwNew}
                    onChange={(e) => { setPwNew(e.target.value); setPwError(''); }}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="At least 4 characters"
                  />
                </div>
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={pwConfirm}
                    onChange={(e) => { setPwConfirm(e.target.value); setPwError(''); }}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Re-enter new password"
                  />
                </div>

                {pwError && (
                  <p className="text-red-400 text-xs font-body">{pwError}</p>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordChange(false)}
                    className="px-5 py-2.5 border border-primary-700/30 rounded-full font-label text-sm tracking-wider text-foreground-300 hover:text-foreground-100 hover:border-primary-500/40 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!pwCurrent || !pwNew || !pwConfirm}
                    className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    Update Password
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Form Modal Overlay */}
      {showForm && (
        <div className="fixed inset-0 z-[60] bg-background-50/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto pt-20 pb-10 px-4">
          <div className="w-full max-w-2xl bg-background-50 border border-primary-800/20 rounded-2xl shadow-xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-foreground-50">
                {editingId ? 'Edit Position' : 'Add New Position'}
              </h2>
              <button
                onClick={closeForm}
                className="w-9 h-9 flex items-center justify-center border border-primary-700/30 rounded-full text-foreground-400 hover:text-foreground-100 hover:border-primary-500/40 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Job Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="e.g. Senior Backend Engineer"
                  />
                </div>
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Department</label>
                  <input
                    type="text"
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="e.g. Engineering"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="e.g. Beirut, Lebanon or Remote"
                  />
                </div>
                <div>
                  <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body focus:outline-none focus:border-primary-500 transition-colors"
                  >
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Internship</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setForm({ ...form, icon })}
                      className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-colors ${
                        form.icon === icon
                          ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                          : 'border-primary-700/30 text-foreground-500 hover:border-primary-500/40 hover:text-foreground-300'
                      }`}
                    >
                      <i className={`${icon} text-lg`} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                  placeholder="Describe the role, responsibilities, and impact..."
                />
              </div>

              <div>
                <label className="block text-foreground-300 text-xs font-label tracking-wider uppercase mb-2">Requirements</label>
                <div className="space-y-2">
                  {form.requirements.map((req, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        type="text"
                        value={req}
                        onChange={(e) => updateReq(i, e.target.value)}
                        className="flex-1 px-4 py-2.5 bg-background-100 border border-primary-700/30 rounded-lg text-foreground-100 text-sm font-body placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder={`Requirement ${i + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeReqField(i)}
                        className="w-9 h-9 flex-shrink-0 flex items-center justify-center border border-primary-700/30 rounded-lg text-foreground-400 hover:text-red-400 hover:border-red-400/40 transition-colors"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addReqField}
                  className="mt-2 inline-flex items-center gap-1.5 text-primary-400 text-xs font-label tracking-wider hover:text-primary-300 transition-colors"
                >
                  <i className="ri-add-line" /> Add Requirement
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-primary-800/20">
              <button
                onClick={closeForm}
                className="px-5 py-2.5 border border-primary-700/30 rounded-full font-label text-sm tracking-wider text-foreground-300 hover:text-foreground-100 hover:border-primary-500/40 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.title.trim() || !form.department.trim() || !form.location.trim() || !form.description.trim()}
                className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-label text-sm tracking-wider hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {editingId ? 'Save Changes' : 'Add Position'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}